<template>
  <div>
    <div class="document-container">
      <div class="columns">
        <div class="column is-three-quarters">
          <div class="document" style="height:100%">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Process Details
                  </div>
                  <div class="header-subtitle">
                    Displays details about processing.
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="content">
              <es-exception v-if="loadingError"
                exceptionMessage="There was an issue. Please try again or contact your administrator."
              />
              <div v-else>
                <es-form>
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon
                          :icon="['fas', 'chart-line']"
                          size="lg"
                        />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Status Statistics
                        </h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <table class="table is-fullwidth" v-if="batch">
                      <thead>
                        <tr>
                          <th class="column-header has-text-grey">
                            Match Status
                          </th>
                          <th class="text-align--right has-text-grey">
                            Record Count
                          </th>
                        </tr>
                      </thead>
                      <tbody v-if="categories.length">
                        <tr
                          class="batch-category has-text-weight-bold"
                          v-for="(category,index) in categories"
                          :key="index"
                        >
                          <td colspan="2" style="border-bottom: 0; padding: 0">
                            <table
                              class="table is-fullwidth"
                              style="margin: 0 1rem 0.5rem 0"
                            >
                              <tbody>
                                <tr class="group-name">
                                  <td
                                    class="has-text-weight-bold removeUnderline"
                                    colspan="2"
                                    style="padding-bottom: 0"
                                  >
                                    {{category.group}}
                                    <!-- Near Match System Label needed -->
                                  </td>
                                </tr>
                                <tr
                                  class="row-border"
                                  v-for="(item,index) in category.list.filter(l => l.count>0)"
                                  :key="index"
                                >
                                  <td
                                    class ="has-text-grey"
                                    style="padding: 0 0 0 1rem"
                                  >
                                    {{item.description}}
                                  </td>
                                  <td
                                    class ="text-align--right"
                                    style="padding: 0 1rem 0 0"
                                  >
                                    {{item.count}}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <es-exception
                      v-if="!categories.length
                        && batch.status === 'COMPLETED'"
                      style="margin:2rem"
                      exceptionMessage="All transactions in this batch were identified as no
                        matches. There are no records to download."
                    />
                    <es-exception
                      v-else-if="!categories.length
                        && batch.status !== 'COMPLETED'"
                      style="margin:2rem"
                      exceptionMessage="No processing status is available."
                    />
                  </template>
                </es-form>
                <es-form style="padding-top: 0">
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon :icon="['fas', 'clock']" size="lg"/>
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Processing Stage
                        </h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <table class="table is-fullwidth" v-if="batch">
                      <thead>
                        <th class="column-header has-text-grey">
                          Stage
                        </th>
                        <th class="text-align--right has-text-grey">
                          Processing Date
                        </th>
                      </thead>
                      <tbody v-if="batch.processingStages.length">
                        <tr
                          class="batch-category"
                          v-for="(item, index) in processingStagesSortAsc"
                          :key="index"
                        >
                          <td class="has-text-grey">
                            {{ item.label }}
                            <!-- Near Match System Label needed -->
                          </td>
                          <td class="text-align--right">
                            {{ item.stageDate | formatDateMMDDYYYYHHMM }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <es-exception
                      v-if="!batch.processingStages.length"
                      style="margin: 2rem"
                      exceptionMessage="No processing stage is available."
                    />
                  </template>
                </es-form>
                <es-form style="padding-top: 0" v-if="batch
                  && batch.status ==='ERRORED'
                  || batch.status ==='ERRORED_VALIDATION'"
                >
                  <template #es-form-header>
                    <div class="header-container">
                      <div class="header-elements">
                        <font-awesome-icon
                          :icon="['fas', 'exclamation-triangle']"
                          size="lg"
                        />
                      </div>
                      <div class="header-elements">
                        <h6 class="title">
                          Errors
                        </h6>
                      </div>
                    </div>
                  </template>
                  <template #es-form>
                    <table
                      class="form-table table is-fullwidth"
                      v-if="errorList"
                    >
                      <thead>
                        <th class="text-align--left has-text-grey">
                          Error Information: See details below. For the file uploaded, please fix
                            the error(s) and resubmit the file.
                        </th>
                      </thead>
                      <tbody>
                        <tr v-for="(error,index) in errorList" :key="index">
                          <td class ="form-row--overflow has-text-grey">
                            {{ error }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                </es-form>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-one-quarter">
          <div class="document margin-bottom"
            v-if="(hasModule('DRMCH:MATCH:RESOLVENM:VIEW')
              && ['COMPLETED_WITH_PENDING'].includes(batch.status))
                || (hasModule('DRMCH:DWNLD:DOWNLOAD')
              && [
              'COMPLETED',
              'ERRORED',
              'COMPLETED_WITH_PENDING',
              ].includes(this.batch.status))
                || (hasModule('DRMCH:MATCH:HISTORY:VIEW'))">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    Next Steps
                  </div>
                </template>
              </EsHeader>
            </div>
            <div
              class="is-flex flex-direction--column flex-align--start"
              style="padding: 1rem"
            >
              <div class="margin-bottom--small">
                <router-link
                  v-if="hasModule('DRMCH:MATCH:RESOLVENM:VIEW')
                    && [
                    'COMPLETED_WITH_PENDING',
                    'RUNNING',
                    'ERRORED',
                    ].includes(batch.status)"
                  :to="{
                    path: '/directmatch/nearMatches',
                    query: { dmBatchId: this.batch.id},
                  }"
                  class="button is-primary has-no-underline"
                >
                  Resolve
                </router-link>
              </div>
              <div class="margin-bottom">
                <router-link
                  v-if="hasModule('DRMCH:DWNLD:DOWNLOAD')
                    && [
                          'COMPLETED',
                          'ERRORED',
                          'COMPLETED_WITH_PENDING',
                          'RUNNING',
                          'LOADED',
                          'QUEUED'
                        ]
                        .includes(this.batch.status)"
                  :to="{
                    path: `/directmatch/batch/${this.batch.id}/download`,
                  }"
                  class="button is-primary has-no-underline"
                >
                  Download
                </router-link>
              </div>
              <div>
                <router-link
                  v-if="hasModule('DRMCH:MATCH:HISTORY:VIEW')"
                  :to="{
                    path: '/directMatch/matchHistory/transactions',
                    query: { batchId: this.batch.id },
                  }"
                >
                  View Transactions
                </router-link>
              </div>
              <div>
                <a
                  v-if="canPauseThisBatch"
                  @click.prevent="pauseThisBatch()"
                >
                  Pause
                </a>
              </div>
              <div>
                <a
                  v-if="canContinueThisBatch"
                  @click.prevent="continueThisBatch()"
                >
                  Continue
                </a>
              </div>
            </div>
          </div>
          <div class="document">
            <div class="header">
              <EsHeader>
                <template #header>
                  <div class="header-title">
                    General Information
                  </div>
                </template>
              </EsHeader>
            </div>
            <div class="content">
              <div>
                <table class="form-table table is-fullwidth" v-if="batch">
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Match Type
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      {{ batch.program.description }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:DISTRICT_CODE`) }}
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                     <div v-if="batch.district">
                       {{ batch.district.location.name }} ( {{ batch.district.code }} )
                     </div>
                     <div v-else>
                        All {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:DISTRICT_CODE`) }}s
                     </div>
                   </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`) }}
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      <template v-if="batch.school">
                        {{ batch.school.location.name }} ( {{ batch.school.code }} )
                      </template>
                      <template v-else>
                        All {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:SCHOOL_CODE`) }}s
                      </template>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Extract Date
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      {{ batch.extractDate  | formatDateMMDDYYYY }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Extract File
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right max-width"
                      :title="batch.profileHeader.fileName"
                    >
                      {{ batch.profileHeader.fileName || 'N/A' }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Transmission ID
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      N/A
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        {{ getFieldLabel(`PER:${defaultPersonType}:DIRECTMATCH:SOURCE_CODE`) }}
                      </label>
                    </td>
                    <td
                      v-if="batch.sourceCode"
                      class="form-row--overflow text-align--right"
                    >
                      {{ batch.sourceCode }}
                    </td>
                    <td v-else class= "text-align--right">
                      N/A
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Creation Date
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      {{ batch.createdDate  | formatDateMMDDYYYYHHMM }}
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Creation User
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      <template v-if="batch.user">
                        {{ batch.user.email }}
                      </template>
                      <template v-else>
                        N/A
                      </template>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Delimiter (Record)
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      <span v-if="batch.fileDetails">
                        {{
                          batch.fileDetails.recordDelimiter
                          ? batch.fileDetails.recordDelimiter
                          : 'N/A'
                        }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        Delimiter (Header)
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      <span v-if="batch.fileDetails">
                        {{
                          batch.fileDetails.headerDelimiter
                          ? batch.fileDetails.headerDelimiter
                          : 'N/A'
                        }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                  <tr class="form-row">
                    <td class="text-align--left">
                      <label class="label">
                        File Version
                      </label>
                    </td>
                    <td class="form-row--overflow text-align--right">
                      <span v-if="batch.fileDetails">
                        {{
                          batch.fileDetails.fileVersion
                          ? batch.fileDetails.fileVersion
                          : 'N/A'
                        }}
                      </span>
                      <span v-else>
                        N/A
                      </span>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faChartLine,
  faClock,
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsException from '@/components/EsException.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faChartLine,
  faClock,
  faExclamationTriangle,
);
export default {
  mixins: [
    NotificationManager,
    PermissionManager,
    FieldLabels,
  ],
  components: {
    EsForm,
    EsHeader,
    EsException,
  },
  props: [
    'batch',
  ],
  data() {
    return {
      processControl: null,
      loadingError: false,
      categories: [],
    };
  },
  computed: {
    ...mapGetters([
      'hasProgramPermission',
    ]),
    ...mapGetters('lookups', [
      'dmTransactionStatuses',
      'defaultPersonType',
    ]),
    errorList() {
      if (this.batch && this.batch.processingMsgs) {
        const splitedErrorList = this.batch.processingMsgs.split('INFO:').filter(val => val !== '');
        return splitedErrorList;
      }
      return null;
    },
    canPauseThisBatch() {
      return (this.processControl
          && this.processControl.status
          && ['QUEUED', 'PROCESSING'].includes(this.processControl.status)
          && ['RUNNING'].includes(this.batch.status));
    },
    canContinueThisBatch() {
      return (this.processControl
          && this.processControl.status
          && ['ERROR'].includes(this.processControl.status)
          && ['ERRORED'].includes(this.batch.status)
          && this.batch.matchType.code === 'DMALL')
          || (['ERRORED'].includes(this.batch.status)
          && this.batch.matchType.code === 'DMDIS');
    },
    processingStagesSortAsc() {
      return this.batch.processingStages.slice().sort((a, b) => a.stageDate - b.stageDate);
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.loadProcessControl(),
        this.loadDmTransactionStatuses(),
        this.loadPersonTypes(),
      ]);
      await this.groupedCategories();
    } catch (error) {
      this.loadingError = true;
    }
  },
  methods: {
    ...mapActions('lookups', [
      'loadDmTransactionStatuses',
      'loadPersonTypes',
    ]),
    async groupedCategories() {
      const ts = JSON.parse(JSON.stringify(this.dmTransactionStatuses));
      this.batch.matchStats.forEach((item) => {
        ts.forEach((group, index) => {
          if (group.list.map(s => s.id).flat().includes(Number(item.matchStatusCode))) {
            ts[index].list.forEach((s, i) => {
              if (s.id === Number(item.matchStatusCode)) {
                ts[index].list[i].count = item.count;
                ts[index].show = true;
              }
            });
          }
        });
      });
      this.categories = ts.filter(x => x.show);
    },
    async loadProcessControl() {
      if (this.batch.matchType.code === 'DMALL') {
        const res = await axios.get('process-control', {
          params: {
            type: 'DM_PROGRAM_VS_ALL_STUDENTS',
          },
        });
        if (res.status === 200) {
          this.processControl = res.data;
          return;
        }
      }
      this.processControl = { status: 'DONE' };
    },
    pauseThisBatch() {
      this.processControl.status = 'ERROR';
      this.updateProcessControl();
      this.showNotification('success',
        ['Request to pause processing of this batch is sent. It may take a few minutes to pause.']);
      this.loadProcessControl();
      this.$emit('refresh');
    },
    async continueThisBatch() {
      try {
        const response = await axios.post('/directmatch/batches', ({
          batchId: this.batch.id,
          matchTypeId: this.batch.matchType.id,
          programCode: this.batch.program.code,
          districtCode: this.batch.districtCode,
          schoolCode: this.batch.schoolCode,
        }));
        const result = response.data;
        this.identifier = result.id;
        this.showNotification('success',
          ['Request to continue processing of this batch is sent. It may take a few minutes to resume processing.']);
        if (this.batch.matchType.code === 'DMALL') this.loadProcessControl();
        this.$emit('refresh');
      } catch (error) {
        this.showNotification('danger',
          ['There was an issue. Please try again or contact your administrator.']);
      }
    },
    async updateProcessControl() {
      const res = await axios.put('process-control', this.processControl);
      this.processControl = res.data;
    },
  },
};
</script>

<style lang="sass" scoped>
  .text-align--right
    text-align: right !important
  .text-align--left
    text-align: left !important
  .removeUnderline
    border-bottom: 1px solid transparent !important
  .column-header
    padding-left: 0px
  .margin-bottom
    margin-bottom: 1rem
  .margin-bottom--small
    margin-bottom: 0.5rem
  .form-row
    line-height: 2rem
    padding-right: 0.5rem
  .row-border
    border-bottom: 1px solid #dee2e6
  .batch-category:last-child
    border-bottom: none
  .batch-category:last-child table .row-border:last-child
    border-bottom: none
  .form-table
    table-layout: fixed
  .form-row--overflow
    overflow: hidden
    text-overflow: ellipsis
  .max-width
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    max-width: 20ch
</style>
